import React, { useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import useAuthStore from "Store/useAuthStore";
import { JD_STATUSES, getJdStatusDisplay, TOOLTIP_STATUS_PROCESSING, TOOLTIP_STATUS_QUEUED, JD_LIST_STATUS_FILTERS, jdListStatusOnFilter, isJdGenerationFailed, isJdQueued, getJdListProgress, isJdTaskRow, getCurrentVersionInfo, CURRENT_VERSION_INFO_TITLE, TOOLTIP_CURRENT_VERSION_INFO, getPersonFilters, matchesPersonFilter } from "constants/jobs.constants";
import useJobsStore from "Store/useJobsStore";
import { AuthContext } from "State/AuthContext";
import { buildFieldConfigMap } from "Utils/formatting";
import Permission from "Components/Permission";
import {
  JobsTableWrapperStyled, STATUS_FILTER_DROPDOWN_CLASS, StatusCellStyled, StatusFilterDropdownGlobal,
} from "./JobsLibraryV2.styles";
import { TOUR_COLUMN_CLASS } from "./JobsLibraryV2.constants";
import SbTable from "uiCore/SbTable/SbTable";
import SbCheckbox from "uiCore/SbCheckbox/SbCheckbox";
import SbTag from "uiCore/SbTag/SbTag";
import { FilterOutlined, SbTooltip } from "uiCore";
import SbModal from "uiCore/SbModal/SbModal";
import SbButton from "uiCore/SbButton/SbButton";
import { retryJdGeneration } from "Api/apiService";
import { showNotification } from "Store/actions/notificationActions";
import { getOwnershipRefusalMessage, isRowHolderOrUnclaimed } from "Utils/jdOwnership";

// PRD §5.1 — tooltip content strings for Jobs Library row elements.
// Status tooltips (getStatusTooltip, TOOLTIP_STATUS_PROCESSING_FAILED) live in
// constants/jobs.constants -- shared with Compensation Management so both
// surfaces describe each jd_status identically.
const TOOLTIP_CRITICAL_ROLE = "Mark this role as business-priority. Priority roles are prioritised in skills planning and highlighted in the library.";
const TOOLTIP_RETRY = "This JD could not be generated. Click to retry — the AI will attempt to regenerate the job description.";
// The Retry link under a failed JD's status (wireframe jobs-library-ownership-v3.html)
const TOOLTIP_RETRY_LINK = "Try generating this JD again (owner only).";
const TOOLTIP_PENDING_APPROVAL_ROW = "This JD is awaiting your approval. Click to open the comparison view and approve or request revisions.";

const RequisitionViewTableV2 = ({
  loading,
  setLoading,
  filteredJobs,
  handleRowClick,
  handleCheckboxChange,
  jobTaskId,
  messages,
  setJobTaskId,
  refreshJobs,
}) => {
  const dispatch = useDispatch();
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const pendingJdId = useJobsStore((state) => state.pendingJdId);
  const { entitlements } = useContext(AuthContext);
  const canRetryJd = useMemo(
    () => entitlements?.find(e => e.name === "jobs_library")?.entitlements?.some(a => a === "retry_jd" || a === "*") ?? false,
    [entitlements]
  );
  const [pendingAnalysisRetryJdModal, setPendingAnalysisRetryJdModal] = useState(false);
  const [generationFailedModel, setGenerationFailedModel] = useState(false);
  // Set when a non-owner opens a failed JD: only its owner may retry it
  const [retryOwnerName, setRetryOwnerName] = useState(null);
  const [retryInProgressModal, setRetryInProgressModal] = useState(false);
  const [retryJobId, setRetryJobId] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  // Only the JD's holder may retry it (its owner, or its delegate while delegated); anyone may on an Unclaimed JD
  // (holder_email / owner_email come from the list's approval data)
  const isOwnerOrUnowned = (record) => isRowHolderOrUnclaimed(record, email);
  // Ask before retrying (the Retry link and a click on the failed row open the same confirmation)
  const openRetry = (record) => {
    setRetryJobId(record.sb_unique_id);
    setPendingAnalysisRetryJdModal(true);
  };

  const handleRetryJd = async () => {
    if (!retryJobId) return;
    setRetryLoading(true);
    try {
      await retryJdGeneration(retryJobId, email);
      dispatch(showNotification("JD generation retried successfully", "success"));
      setJobTaskId?.({ task_id: retryJobId, job_id: retryJobId });
      setLoading(true);
    } catch (err) {
      const refusal = getOwnershipRefusalMessage(err);
      if (refusal) {
        // Someone else holds the JD now, or it is outside the caller's view scope (a stale list): say so and
        // reload the list
        dispatch(showNotification(refusal, "error"));
        refreshJobs?.();
      } else {
        dispatch(showNotification("Failed to retry JD generation. Please try again.", "error"));
      }
    } finally {
      setRetryLoading(false);
      setPendingAnalysisRetryJdModal(false);
      setRetryJobId(null);
    }
  };


  // Build a fieldMap keyed by field_name for the 'req_no' ui_element.
  // Using find-by-type (not hardcoded index) so labels remain correct if the
  // server ever reorders ui_elements. All field_names in req_no are unique so
  // a flat map is safe here — no duplicate-key risk.
  const fieldMap = useMemo(
    () => buildFieldConfigMap(orgMetaData, 'req_no'),
    [orgMetaData],
  );
  const isSubFunctionVisible = fieldMap?.sub_function?.visible !== false;

  const getJobTitle = (record) => record.job_title_for_jd || record.job_title || "";

  const columns = [
    {
      title: 'Status',
      dataIndex: 'jd_status',
      onHeaderCell: () => ({ className: TOUR_COLUMN_CLASS.status }),
      filters: JD_LIST_STATUS_FILTERS,
      onFilter: jdListStatusOnFilter,
      // Show every status without an inner scroll (StatusFilterDropdownGlobal)
      filterDropdownProps: { rootClassName: STATUS_FILTER_DROPDOWN_CLASS },
      render: (status, record) => {
        const isFailed = isJdGenerationFailed(record);
        const taskProgress = Number(messages?.progress || 0);
        const isTaskRow =
          isJdTaskRow(jobTaskId, record);
        // At progress 100 the row is still PENDING_ANALYSIS for a few seconds while BigQuery
        // catches up — that is generation finishing, not a failure, so don't flash red.
        const showProcessingFailed = isFailed && (!isTaskRow || taskProgress === 0 || (taskProgress === 100 && status !== JD_STATUSES.PENDING_ANALYSIS));
        // email: the Draft pill's tooltip differs for whoever may see the drafts and everyone else
        const { label, color, tooltip, style } = getJdStatusDisplay(record, showProcessingFailed, email);

        const tag = (
          <SbTooltip content={tooltip}>
            <SbTag color={color} style={style}>{label}</SbTag>
          </SbTooltip>
        );
        // A failed JD its holder (or anyone, when Unclaimed) may retry: the wireframe's Retry link under the status
        if (!(showProcessingFailed && canRetryJd && isOwnerOrUnowned(record))) return tag;
        return (
          <StatusCellStyled>
            {tag}
            <SbTooltip content={TOOLTIP_RETRY_LINK}>
              <SbButton
                type="link"
                size="small"
                data-testid="jd-retry-link"
                stopPropagation
                onClick={() => handleFailedRowClick(record)}
              >
                Retry
              </SbButton>
            </SbTooltip>
          </StatusCellStyled>
        );
      },
    },
    {
      title: fieldMap['job_code']?.label,
      dataIndex: "sb_requisition_number",
    },
    {
      title: fieldMap['job_title']?.label,
      dataIndex: "job_title",
      render: (_, record) => getJobTitle(record),
      // Filter on the displayed title (job_title_for_jd falls back to job_title).
      filters: [...new Set((filteredJobs || []).map(getJobTitle).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
        .map((value) => ({ text: value, value })),
      onFilter: (value, record) => getJobTitle(record) === value,
    },
    {
      title: fieldMap['business_unit']?.label,
      dataIndex: "business_unit",
    },
    {
      title: fieldMap['location']?.label,
      dataIndex: "location",
    },
    {
      title: fieldMap['function']?.label,
      dataIndex: "function",
    },
    ...(isSubFunctionVisible
      ? [
        {
          title: fieldMap['sub_function']?.label,
          dataIndex: "sub_function",
        },
      ]
      : []),
    {
      title: fieldMap['job_level']?.label,
      dataIndex: "job_level",
    },
    {
      // "Other Info" renamed "Current Version Info" (Save as Draft design, Jobs Library short spec; wireframe)
      title: <SbTooltip content={TOOLTIP_CURRENT_VERSION_INFO}><span>{CURRENT_VERSION_INFO_TITLE}</span></SbTooltip>,
      dataIndex: "version_info",
      // Filter by person (wireframe): every JD a person owns, is the delegate on or has its drafts shared with them
      filters: getPersonFilters(filteredJobs, email),
      filterSearch: true,
      onFilter: matchesPersonFilter,
      render: (_, record) => {
        const info = getCurrentVersionInfo(record, email);
        return (
          <div data-testid="current-version-info">
            <SbTooltip content={info.hover}><div>{info.line}</div></SbTooltip>
            <div>Owner: {info.unclaimed ? <i>Unclaimed</i> : info.owner}{info.ownerIsYou && ' (You)'}</div>
            {info.delegate && <div>Delegated to: {info.delegate}{info.delegateIsYou && ' (You)'}</div>}
            {info.sharedWithYou && <div>Draft shared with you</div>}
          </div>
        );
      },
    },
    {
      title: fieldMap['critical_role']?.label,
      dataIndex: "critical_role",
      align: "center",
      onHeaderCell: () => ({ className: TOUR_COLUMN_CLASS.criticalRole }),
      filters: [
        { text: "Priority", value: true },
        { text: "Non-Priority", value: false },
      ],
      onFilter: (value, record) =>
        (record.critical_role === true || record.critical_role === "true") === value,
      render: (_, record) => (
        <Permission resource="jobs_library" actions={["mark_critical_role"]}>
          <SbTooltip content={TOOLTIP_CRITICAL_ROLE}>
            <SbCheckbox
              checked={record.critical_role === true || record.critical_role === "true"}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => handleCheckboxChange(e, record.sb_unique_id)}
            />
          </SbTooltip>
        </Permission>
      ),
    },
  ];

  const getRowClassName = (record) => {
    const isLoadingRow = record.job_id && record.job_id === pendingJdId;
    const isJobApprovalPending =
      record.jd_status === "PENDING_APPROVAL" && record.assigned_approver_email === email;
    const isJobNeedRevision =
      record.jd_status === "NEEDS_REVISION" && record.user_id === email;
    const taskProgress = Number(messages?.progress || 0);
    const isTaskRow =
      isJdTaskRow(jobTaskId, record);
    const listProgress = getJdListProgress(record);
    const showProgressBar = isTaskRow
      ? (taskProgress > 0 && taskProgress < 100) ||
        (taskProgress === 100 && [JD_STATUSES.NEW, JD_STATUSES.DRAFT, JD_STATUSES.GENERATED].includes(record?.jd_status))
      : listProgress > 0 && listProgress < 100;

    let cls;
    if (isLoadingRow) {
      cls = "jobs-table-content jd-in-progress-row";
    } else if (isJobApprovalPending) {
      cls = "jobs-table-content job-approval-pending";
    } else if (isJobNeedRevision) {
      cls = "jobs-table-content needs-revision-jd-row";
    } else if (record.jd_status !== "PUBLISHED") {
      cls = "jobs-table-content jd-unpublished";
    } else {
      cls = "jobs-table-content";
    }

    return `${cls}${showProgressBar ? " fill-bg" : ""}`;
  };

  // A failed JD clicked (its row or its Retry link): wait while another retry is running (a second one would lose
  // track of the first), else ask before retrying, or say who may retry it
  const handleFailedRowClick = (record) => {
    const taskProgress = Number(messages?.progress || 0);
    if (taskProgress > 0 && taskProgress < 100) {
      if (!isJdTaskRow(jobTaskId, record)) setRetryInProgressModal(true);
    } else if (canRetryJd && isOwnerOrUnowned(record)) {
      openRetry(record);
    } else if (canRetryJd) {
      setRetryOwnerName(holderName(record));
      setGenerationFailedModel(true);
    } else {
      setGenerationFailedModel(true);
    }
  };

  // the JD's holder: its delegate while delegated, else its owner
  const holderName = (record) => (record?.delegate_email && record?.delegate_name) || record?.current_owner
    || 'the owner of this JD';
  const ownerRetryMessage = (record) => `Only ${holderName(record)} can retry this JD.`;

  const getRowTitle = (record) => {
    const isLoadingRow = record.job_id && record.job_id === pendingJdId;
    const isJobApprovalPending =
      record.jd_status === "PENDING_APPROVAL" && record.assigned_approver_email === email;
    const isTaskRow =
      isJdTaskRow(jobTaskId, record);
    const isFailed = isJdGenerationFailed(record);
    const isRetryableFailure = isFailed && canRetryJd;

    // PRD §5.1 — Assigned approver sees the awaiting-approval prompt.
    if (isJobApprovalPending) return TOOLTIP_PENDING_APPROVAL_ROW;
    if (isLoadingRow) return "Thinking...";
    if (isTaskRow) return Number(messages?.progress) === 100 ? (record?.jd_status === JD_STATUSES.GENERATION_FAILED ? 'Processing Failed' : (messages?.extra_info?.is_retrying ? 'JD Retried' : 'JD Created')) : `${messages?.extra_info?.is_retrying ? "Retrying" : "Creating"} JD - ${messages?.status || ''}`;
    // PRD §5.1 — Retry-eligible failure rows surface the retry prompt for users
    // with the retry_jd entitlement.
    if (isRetryableFailure) return isOwnerOrUnowned(record) ? TOOLTIP_RETRY : ownerRetryMessage(record);
    if (record?.jd_status === JD_STATUSES.PENDING_ANALYSIS && !isFailed) {
      return isJdQueued(record) ? TOOLTIP_STATUS_QUEUED : TOOLTIP_STATUS_PROCESSING;
    }
    return "View and Edit Job Description";
  };

  return (
    <JobsTableWrapperStyled>
      <StatusFilterDropdownGlobal />
      <SbTable
        columns={columns}
        dataSource={filteredJobs}
        rowKey={(record, index) => record.sb_unique_id ?? index}
        loading={loading}
        sticky
        rowClassName={getRowClassName}
        pagination={{
          showTotal: (total) => {
            const hasActiveFilters = total < (filteredJobs?.length ?? 0);
            return (
              <div className="paic-pagination-total">
                <FilterOutlined />
                <span className={hasActiveFilters ? "is-active-text" : "is-empty-text"}>
                  {hasActiveFilters
                    ? `${total} Roles selected`
                    : "0 Roles selected. Please select at least one role"}
                </span>
              </div>
            );
          },
        }}
        onRow={(record) => {
          const isLoadingRow = record.job_id && record.job_id === pendingJdId;
          const isFailed = isJdGenerationFailed(record);
          const isProcessing = record?.jd_status === JD_STATUSES.PENDING_ANALYSIS && !isFailed;
          const isTaskRow =
            isJdTaskRow(jobTaskId, record);
          return {
            title: getRowTitle(record),
            // Live progress for the tracked JD; other generating JDs use the progress from the list.
            style: { "--progress": `${isTaskRow ? Number(messages?.progress || 0) : getJdListProgress(record)}%` },
            onClick: () => {
              // Still generating (bulk queue or live task): nothing to open or retry yet.
              if (isLoadingRow || isProcessing) return;
              if (!isFailed) handleRowClick(record);
              else handleFailedRowClick(record);
            },
          };
        }}
      />
      {/* Retry JD Generation — users with retry_jd entitlement */}
      <SbModal
        open={pendingAnalysisRetryJdModal}
        title="Retry JD Generation"
        onCancel={() => { setPendingAnalysisRetryJdModal(false); setRetryJobId(null); }}
        footer={
          <>
            <SbButton onClick={() => { setPendingAnalysisRetryJdModal(false); setRetryJobId(null); }}>
              Cancel
            </SbButton>
            <SbButton buttonType="primary" loading={retryLoading} onClick={handleRetryJd}>
              Retry JD
            </SbButton>
          </>
        }
      >
        This job description could not be generated. Would you like to retry the JD generation?
      </SbModal>

      {/* Retry already in progress */}
      <SbModal
        open={retryInProgressModal}
        title="Retry Already In Progress"
        onCancel={() => setRetryInProgressModal(false)}
        footer={
          <SbButton buttonType="primary" onClick={() => setRetryInProgressModal(false)}>
            OK
          </SbButton>
        }
      >
        One JD is already being retried. Please wait for it to complete.
      </SbModal>

      {/* JD Generation Failed — users without retry_jd entitlement */}
      <SbModal
        open={generationFailedModel}
        title="JD Generation Failed"
        onCancel={() => { setGenerationFailedModel(false); setRetryJobId(null); setRetryOwnerName(null); }}
        footer={
          <SbButton buttonType="primary" onClick={() => { setGenerationFailedModel(false); setRetryJobId(null); setRetryOwnerName(null); }}>
            OK
          </SbButton>
        }
      >
        {retryOwnerName
          ? `This job description could not be generated. Only ${retryOwnerName} can retry this JD.`
          : 'This job description could not be generated. Please contact SkillsBridge support to retry the analysis.'}
      </SbModal>
    </JobsTableWrapperStyled>
  );
};

export default RequisitionViewTableV2;
